import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from 'react';

import { Builder, BuilderBlocks } from '@builder.io/react';
import merge from 'lodash.merge';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { Router } from '@techstyle/next-routes';
import { updateSessionDetail } from '@techstyle/redux-core';

import { trackSignUpModal } from '../../actions/auth';
import {
  trackQuizScreen,
  trackQuizStart,
  trackQuizTraits,
} from '../../actions/quiz';
import { SignUpMethod } from '../../constants/auth';
import { desktop, mobile } from '../../styles';
import { getQuizSizeSteps } from '../../utils/getQuizSizeSteps';
import { BuilderQuizCompleteConfig } from '../BuilderQuizComplete/BuilderQuizComplete.builder';
import { BuilderQuizSizePreferenceConfig } from '../BuilderQuizSizePreference/BuilderQuizSizePreference.builder';
import { NextButton } from '../ProductQuizSharedComponents';
import ToastMessages from '../ToastMessages/ToastMessages';

import BuilderQuizBanner from './BuilderQuizBanner';
import QuizNavBar from './QuizNavBar';
import QuizProgressBar from './QuizProgressBar';

const QuizWrapper = styled.div`
  height: 100%;

  .builder-text > h1 {
    ${props => props.theme.header.variants.h1.textStyles}
  }

  .builder-text > h2 {
    ${props => props.theme.header.variants.h2.textStyles}
  }

  .builder-text > h3 {
    ${props => props.theme.header.variants.h3.textStyles}
  }

  @media only screen and (max-width: 665px) and (orientation: Portrait) {
    height: 100vh;
    overflow-y: hidden;
  }

  @media only screen and (max-width: 665px) and (orientation: portrait) {
    min-height: ${({ isEditing }) => (isEditing ? '100%' : '100vh')};
    overflow-y: hidden;
  }

  @media only screen and (max-width: 995px) and (orientation: landscape) {
    min-height: ${({ isEditing }) => (isEditing ? '100%' : '100vh')};
    overflow-y: hidden;
  }

  ${mobile`
    @media (orientation: portrait) {
      min-height: ${({ isEditing }) => (isEditing ? '100%' : '100vh')};
      ${({ isEditing }) => isEditing && ` padding-top: 84px;`}
    }
  `}
`;

const QuizContentWrapper = styled.div`
  display: flex;
  height: 100%;
  flex: 1;
  flex-direction: column;
  width: 100%;
  z-index: auto;
  font-weight: 300;

  ${desktop`
  border-radius: 4px;
  ${({ backgroundImage }) =>
    backgroundImage &&
    `
        background-image: url(${backgroundImage});
        background-size: cover;
        background-position: center;
    `}
  `}

  ${mobile`
    ${({ mobileBackgroundImage }) =>
      mobileBackgroundImage &&
      `
        background-image: url(${mobileBackgroundImage});
        background-size: cover;
        background-position: center;
        overflow-y: scroll;
    `}
  `}
`;

const StyledToastMessages = styled(ToastMessages)`
  top: 0;
  z-index: ${({ theme }) => theme.zIndex.toast};
`;

const StyledNextButton = styled(NextButton)`
  button {
    max-width: 45vw;
    min-width: unset;
  }
  ${({ disabled }) =>
    disabled &&
    `
      background-color: ${({ theme }) => theme.colors.active};
      border-color: ${({ theme }) => theme.colors.active};
      color: ${({ theme }) => theme.colors.white};
      opacity: 0.5;
      button {
        cursor: not-allowed;
      }
    `}
  border: 1px solid black;
  min-width: 128px;
  text-align: center;
  ${mobile`
      button { width: 100%; }
    `}
`;

const StepSelector = styled.div`
  display: flex;
  position: absolute;
  width: 100%;
  justify-content: space-between;
  top: -48px;
  z-index: 1;

  > span {
    position: absolute;
    top: -24px;
    font-weight: bold;
    font-size: 16px;
  }

  ${mobile`
    top: 12px;

    > span {
      top: 48px;
    }
  `}

  top: -56px;
  ${mobile`
      top: 4px;

      > span {
        top: 52px;
      }
    `}
  color: #fff;
  text-shadow: 1px 0 0 #000, 0 -1px 0 #000, 0 1px 0 #000, -1px 0 0 #000;
`;

const StyledBuilderBlocks = styled(BuilderBlocks)`
  min-height: 100%;
  ${mobile`
    min-height: auto;
    box-sizing: border-box;
    padding-bottom: ${({ showBanner }) => (showBanner ? '120px' : '80px')}
  `}
  position: relative;
  ${({ showBanner }) => !showBanner && 'border-radius: 4px;'}
  img {
    ${({ showBanner }) => !showBanner && 'border-radius: 4px;'}
  }
`;

// 1. Create Context to control builderquiz state
export const QuizContext = React.createContext();

const BuilderQuiz = ({
  builderBlock,
  exitPath,
  skipToStep,
  steps = [],
  showBanner,
  bannerTitle,
  bannerSubtitle,
  bannerBGColor,
}) => {
  // get the first step where showProgress is enabled
  const firstStepIndex = steps.findIndex(step => !!step.showProgress);
  const [enableNextButton, setEnableNextButton] = useState(false);
  const { isEditing, isPreviewing } = Builder;
  const isLive = !isEditing && !isPreviewing;
  const [visitedScreens, setVisitedScreens] = useState({});

  const isFirstRender = useRef(true);

  const [quizAnswers, setQuizAnswers] = useState({
    zipCode: '',
  });
  const [goingBack, setGoingBack] = useState(false);
  const dispatch = useDispatch();
  const router = useRouter();

  const currentRoute = [
    ...(router.query.stepId ?? []),
    ...(router.query.slug ?? []),
  ].join('/');

  const sizeScreenInformation = useMemo(() => {
    const sizeStep = steps.find(
      step =>
        step.content[0].component?.name === BuilderQuizSizePreferenceConfig.name
    );

    if (!sizeStep) {
      return [];
    }

    const sizeStepProps = sizeStep.content[0].component?.options;
    if (!sizeStepProps) {
      return [];
    }

    const productTypesQuestionName = sizeStepProps.productTypesQuestionName;
    if (!productTypesQuestionName) {
      return [];
    }

    const productTypes = quizAnswers[productTypesQuestionName] ?? {};

    // TODO: Get size information instead, and return both screen name and screen questions
    const sizeSteps = getQuizSizeSteps({ productTypes, isEditing });
    return sizeSteps.flatMap(stepName => {
      switch (stepName) {
        case 'bra':
          return [
            {
              screenName:
                sizeStepProps.braSizeScreenName ||
                sizeStepProps.braSizeQuestionName,
              screenQuestion: sizeStepProps.braSizeSubtitle,
            },
          ];
        case 'bralette':
          return [
            {
              screenName:
                sizeStepProps.braletteSizeScreenName ||
                sizeStepProps.braletteSizeQuestionName,
              screenQuestion: sizeStepProps.braletteSizeSubtitle,
            },
          ];
        case 'undie':
          return [
            {
              screenName:
                sizeStepProps.undieSizeScreenName ||
                sizeStepProps.undieSizeQuestionName,
              screenQuestion: sizeStepProps.undieSizeSubtitle,
            },
          ];
        default:
          return [];
      }
    });
  }, [quizAnswers, steps, isEditing]);

  const screens = useMemo(
    () =>
      steps.flatMap((step, index) => {
        const isSizeStep =
          step.content[0].component?.name ===
          BuilderQuizSizePreferenceConfig.name;

        if (isSizeStep) {
          if (sizeScreenInformation.length > 0) {
            return [
              ...sizeScreenInformation.map(({ screenName }) => ({
                ...step,
                screenName,
                stepIndex: index,
              })),
            ];
          } else {
            return [];
          }
        }

        return [{ ...step, stepIndex: index }];
      }),
    [sizeScreenInformation, steps]
  );

  const currentScreenIndex = useMemo(() => {
    const index = screens.findIndex(
      screen => currentRoute === screen.screenName
    );

    return index !== -1 ? index : 0;
  }, [currentRoute, screens]);

  const currentScreen = useMemo(
    () => ({
      ...screens[currentScreenIndex],
      hasVisited: !!visitedScreens[currentScreenIndex],
    }),
    [screens, currentScreenIndex, visitedScreens]
  );

  const currentStepIndex = useMemo(
    () => currentScreen.stepIndex,
    [currentScreen]
  );

  const navigate = useCallback(
    stepId => {
      const builderPage = router.pathname.includes('[page]');
      return router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            slug: builderPage ? [] : stepId,
            page: router.query.page ? [router.query.page] : [],
            stepId: builderPage ? stepId : [],
          },
        },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );

  useEffect(() => {
    if (!isEditing) {
      dispatch(updateSessionDetail({ name: 'take_quiz', value: '' }));
      dispatch(trackQuizStart());
    }
  }, [dispatch, isEditing]);

  const currentScreenQuestion = useMemo(() => {
    const currentScreenComponent = currentScreen.content[0].component;

    if (!currentScreenComponent) {
      return;
    }

    let question;

    if (currentScreenComponent.name === BuilderQuizSizePreferenceConfig.name) {
      question = sizeScreenInformation.find(
        ({ screenName }) => screenName === currentScreen.screenName
      )?.screenQuestion;
    } else if (currentScreenComponent.name === 'Text') {
      question = currentScreenComponent.options.text;
    }

    return question;
  }, [currentScreen, sizeScreenInformation]);

  // We keep the last tracked path to avoid double triggering of events
  const lastTrackedQuizPath = React.useRef(router.asPath);
  useEffect(() => {
    if (!isLive || lastTrackedQuizPath.current === router.asPath) {
      return;
    }

    dispatch(
      trackQuizScreen(
        router.asPath,
        currentScreenQuestion,
        currentScreenIndex + 1 - firstStepIndex
      )
    );

    lastTrackedQuizPath.current = router.asPath;
  }, [
    isLive,
    dispatch,
    router.asPath,
    currentScreenQuestion,
    currentScreenIndex,
    firstStepIndex,
  ]);

  useEffect(() => {
    if (isFirstRender.current) {
      return;
    }

    if (
      currentScreen.content[0]?.component?.name ===
      BuilderQuizCompleteConfig.name
    ) {
      dispatch(trackSignUpModal(SignUpMethod.QUIZ));
    }
  }, [currentScreen, dispatch]);

  useEffect(() => {
    if (!isFirstRender.current) {
      return;
    }

    const isCurrentRouteValid = screens.some(
      screen => currentRoute === screen.screenName
    );

    if (currentScreenIndex === 0 && isCurrentRouteValid) {
      isFirstRender.current = false;
    } else {
      navigate('');
    }
  }, [currentScreenIndex, navigate, router, currentRoute, screens]);

  if (!screens || !screens.length) {
    return null;
  }

  const skipToStepIndex = screens.findIndex(
    ({ stepIndex }) => stepIndex === skipToStep
  );

  const skipQuizTargetIndex = screens
    ? Math.min(skipToStepIndex, screens.length)
    : null;

  const shouldShowSkipQuizLink =
    skipQuizTargetIndex && currentScreenIndex < skipQuizTargetIndex;

  const handleExit = () => {
    if (!isEditing) {
      Router.push(exitPath || '/');
    }
  };

  const handleSkipQuiz = () => {
    if (!isEditing) {
      dispatch(updateSessionDetail({ name: 'skip_quiz', value: '' }));
    }

    const targetScreen = screens[skipQuizTargetIndex];

    if (targetScreen) {
      navigate(targetScreen.screenName);
    }
  };

  const advanceScreen = () => {
    const nextScreenIndex = currentScreenIndex + 1;
    const nextScreen = screens[nextScreenIndex];
    if (!nextScreen.screenName) {
      return;
    }

    setVisitedScreens(prev => ({ ...prev, [currentScreenIndex]: true }));
    setGoingBack(false);
    navigate(nextScreen.screenName);
  };

  const previousScreen = () => {
    if (currentScreenIndex > 0) {
      router.back();
      setGoingBack(true);
    }
  };

  const {
    showNavigation,
    showProgress,
    showSkipQuiz,
    navigationTextColor,
    showBackButton,
  } = currentScreen;

  const setEnableButton = value => {
    setEnableNextButton(value);
  };

  const onAnswerChange = (
    value,
    questionName,
    autoAdvance,
    goToNextScreen = advanceScreen
  ) => {
    if (!isEditing) {
      // if questionName already exists, merge into the existing object. Otherwise, add it
      const updatedAnswers = { ...quizAnswers };

      if (
        quizAnswers[questionName] &&
        // only merge if answer is an object, otherwise just replace
        typeof quizAnswers[questionName] === 'object'
      ) {
        merge(updatedAnswers[questionName], value);
      } else {
        updatedAnswers[questionName] = value;
      }
      setQuizAnswers(updatedAnswers);
      if (autoAdvance && !currentScreen.hasVisited) {
        // delay transition to next step when autoadvance is enabled
        setTimeout(() => goToNextScreen(), 500);
      }
    }
  };

  return (
    <QuizContext.Provider
      value={{
        advanceScreen,
        previousScreen,
        setEnableButton,
        onAnswerChange,
        quizAnswers,
        isEditing,
        goingBack,
        currentScreenIndex,
        trackQuizTraits,
        trackQuizScreen,
        enableNextButton:
          currentScreenIndex === 0 || isEditing ? true : enableNextButton,
        currentRoute,
        handleSkipQuiz,
      }}
    >
      <QuizWrapper isEditing={isEditing}>
        {isEditing && (
          <StepSelector>
            <span>Editor only. Step number: {currentScreenIndex + 1}</span>
            <StyledNextButton
              onClick={previousScreen}
              disabled={currentScreenIndex === 0}
              isValid
            >
              Previous
            </StyledNextButton>
            <StyledNextButton
              onClick={advanceScreen}
              disabled={currentScreenIndex === screens.length - 1}
              isValid
            >
              Next
            </StyledNextButton>
          </StepSelector>
        )}
        <StyledToastMessages />
        {showBanner && (
          <BuilderQuizBanner
            bannerBGColor={bannerBGColor}
            bannerTitle={bannerTitle}
            bannerSubtitle={bannerSubtitle}
          />
        )}
        {showProgress && (
          <QuizProgressBar
            currentStep={currentScreenIndex + 1 - firstStepIndex}
            totalSteps={screens.length - firstStepIndex}
          />
        )}
        {showNavigation && (
          <QuizNavBar
            previousScreen={previousScreen}
            handleSkipQuiz={handleSkipQuiz}
            handleExit={handleExit}
            shouldShowCloseLink={!shouldShowSkipQuizLink || !showSkipQuiz}
            shouldShowSkipQuizLink={shouldShowSkipQuizLink && showSkipQuiz}
            navigationTextColor={navigationTextColor}
            shouldShowBackLink={currentScreenIndex > 0 && showBackButton}
          />
        )}
        <QuizContentWrapper
          backgroundImage={currentScreen?.backgroundImage}
          mobileBackgroundImage={
            currentScreen?.mobileBackgroundImage ||
            currentScreen?.backgroundImage
          }
        >
          {currentScreen && (
            <StyledBuilderBlocks
              parentElementId={builderBlock && builderBlock.id}
              dataPath={`component.options.steps.${currentStepIndex}.content`}
              blocks={currentScreen.content || currentScreen}
              showBanner={showBanner}
            />
          )}
        </QuizContentWrapper>
      </QuizWrapper>
    </QuizContext.Provider>
  );
};

BuilderQuiz.propTypes = {
  builderBlock: PropTypes.any,
  exitPath: PropTypes.string,
  skipToStep: PropTypes.number,
  steps: PropTypes.array,
};

export default BuilderQuiz;
